import {
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  CogIcon,
  HomeIcon,
  KeyIcon,
  ShoppingBagIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { BreadcrumbItem, Breadcrumbs } from '@heroui/breadcrumbs';
import { Button } from '@heroui/button';
import { Card, CardBody } from '@heroui/card';
import { Divider } from '@heroui/divider';
import React from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const AccountLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/account/dashboard',
      icon: ChartBarIcon,
    },
    {
      name: 'Profile',
      href: '/account/profile',
      icon: UserIcon,
    },
    {
      name: 'Orders',
      href: '/account/orders',
      icon: ShoppingBagIcon,
    },
    {
      name: 'Support Tickets',
      href: '/account/tickets',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      name: 'Security',
      href: '/account/security',
      icon: KeyIcon,
    },
    {
      name: 'Settings',
      href: '/account/settings',
      icon: CogIcon,
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Get current page name for breadcrumbs
  const getCurrentPageName = () => {
    const currentNav = navigation.find(nav => nav.href === location.pathname);
    return currentNav?.name || 'Account';
  };

  return (
    <div className='container mx-auto px-4 py-8'>
      <div className='max-w-7xl mx-auto'>
        {/* Breadcrumbs */}
        <div className='mb-6'>
          <Breadcrumbs
            size='sm'
            separator={<ChevronRightIcon className='w-3 h-3' />}
          >
            <BreadcrumbItem>
              <Link
                to='/'
                className='flex items-center gap-1 text-foreground-500 hover:text-foreground'
              >
                <HomeIcon className='w-4 h-4' />
                <span>Home</span>
              </Link>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <span className='text-foreground'>My Account</span>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <span className='text-primary font-medium'>
                {getCurrentPageName()}
              </span>
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>

        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-gray-900'>My Account</h1>
          <p className='text-gray-600 mt-2'>
            Welcome back, {user?.firstName} {user?.lastName}
          </p>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-4 gap-8'>
          {/* Sidebar Navigation */}
          <div className='lg:col-span-1'>
            <Card>
              <CardBody className='p-0'>
                <div className='p-6 border-b'>
                  <div className='flex items-center space-x-3'>
                    {user?.profileImageUrl ? (
                      <img
                        src={user.profileImageUrl}
                        alt='Profile'
                        className='w-12 h-12 rounded-full object-cover'
                      />
                    ) : (
                      <div className='w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center'>
                        <UserIcon className='w-6 h-6 text-primary-600' />
                      </div>
                    )}
                    <div>
                      <h3 className='font-semibold text-gray-900'>
                        {user?.firstName} {user?.lastName}
                      </h3>
                      <p className='text-sm text-gray-500'>{user?.email}</p>
                    </div>
                  </div>
                  {!user?.emailConfirmed && (
                    <div className='mt-3 p-2 bg-warning-50 border border-warning-200 rounded-lg'>
                      <p className='text-xs text-warning-600'>
                        Please verify your email address
                      </p>
                    </div>
                  )}
                </div>

                <nav className='p-4 space-y-2'>
                  {navigation.map(item => {
                    const Icon = item.icon;
                    return (
                      <NavLink
                        key={item.name}
                        to={item.href}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          isActive(item.href)
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <Icon className='w-5 h-5' />
                        <span>{item.name}</span>
                      </NavLink>
                    );
                  })}
                </nav>

                <Divider />

                <div className='p-4'>
                  <Button
                    variant='ghost'
                    color='danger'
                    onClick={logout}
                    className='w-full justify-start'
                  >
                    Sign Out
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Main Content */}
          <div className='lg:col-span-3'>
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountLayout;
